import React, { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Search, ShieldAlert, CheckCircle2, XCircle } from 'lucide-react';
import { fmtDate } from '@/lib/api';

function ScoreBar({ score }) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const color = s >= 95 ? 'bg-[hsl(0_72%_45%)]' : s >= 85 ? 'bg-[hsl(30_90%_45%)]' : 'bg-[hsl(43_84%_38%)]';
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-2 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${s}%` }} />
      </div>
      <span className="tabular-nums text-xs font-medium">{s.toFixed(0)}</span>
    </div>
  );
}

function StatusChip({ status }) {
  const s = String(status || '').toUpperCase();
  const cfg = {
    OPEN: 'bg-[hsl(0_72%_95%)] text-[hsl(0_72%_35%)] border-[hsl(0_60%_85%)]',
    CLEARED: 'bg-[hsl(152_45%_94%)] text-[hsl(152_45%_24%)] border-[hsl(152_35%_80%)]',
    ESCALATED: 'bg-[hsl(30_90%_92%)] text-[hsl(30_90%_28%)] border-[hsl(30_60%_80%)]',
    BLOCKED: 'bg-[hsl(0_72%_95%)] text-[hsl(0_72%_25%)] border-[hsl(0_60%_75%)]',
  }[s] || 'bg-[hsl(210_18%_94%)] text-[hsl(222_47%_11%)] border-[hsl(214_20%_88%)]';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium ${cfg}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {s}
    </span>
  );
}

export default function AdminAmlPage() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('OPEN');
  const [screenName, setScreenName] = useState('');
  const [screenResult, setScreenResult] = useState(null);
  const [screening, setScreening] = useState(false);
  const [reviewFlag, setReviewFlag] = useState(null); // opened flag in dialog
  const [reviewStatus, setReviewStatus] = useState('CLEARED');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter === 'ALL' ? {} : { status: filter };
      const r = await adminApi.get('/admin/aml/flags', { params });
      setItems(r.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const runScreen = async () => {
    if (!screenName.trim()) return;
    setScreening(true);
    try {
      const r = await adminApi.post('/admin/aml/screen-by-name', { name: screenName.trim() });
      setScreenResult(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setScreening(false); }
  };

  const openReview = (flag) => {
    setReviewFlag(flag);
    setReviewStatus('CLEARED');
    setReviewNotes('');
  };

  const submitReview = async () => {
    if (!reviewFlag) return;
    setReviewBusy(true);
    try {
      await adminApi.post(`/admin/aml/flags/${reviewFlag.id}/resolve`, { status: reviewStatus, notes: reviewNotes });
      toast.success(t('admin_aml.flag_updated'));
      setReviewFlag(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setReviewBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-aml-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">{t('admin_aml.title')}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{t('admin_aml.subtitle')}</p>
        </div>
      </div>

      <Card className="bg-white">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-3">
            <ShieldAlert className="h-5 w-5 text-[hsl(var(--brand-gold-2))]" />
            <h2 className="font-serif text-lg text-[hsl(var(--brand-navy-2))]">{t('admin_aml.screen_title')}</h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <Label htmlFor="aml-screen-name">{t('admin_aml.screen_name_label')}</Label>
              <Input
                id="aml-screen-name"
                data-testid="admin-aml-screen-input"
                placeholder="e.g. John Smith"
                value={screenName}
                onChange={e => setScreenName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && runScreen()}
              />
            </div>
            <div className="sm:pt-6">
              <Button
                onClick={runScreen}
                disabled={screening || !screenName.trim()}
                data-testid="admin-aml-screen-button"
                className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))]"
              >
                <Search className="h-4 w-4 mr-2" />{t('admin_aml.screen_action')}
              </Button>
            </div>
          </div>
          {screenResult && (
            <div className="mt-4 border-t pt-4" data-testid="admin-aml-screen-result">
              <div className="text-xs text-[hsl(var(--muted-foreground))] mb-2">
                {t('admin_aml.checked_against', { n: screenResult.checked_count })}
              </div>
              {screenResult.matched ? (
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-[hsl(0_72%_35%)] flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4" />
                    {t('admin_aml.matches_found', { n: screenResult.matches.length })}
                  </div>
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-[hsl(var(--secondary))]">
                        <tr>
                          <th className="p-2 text-left">{t('admin_aml.matched_name')}</th>
                          <th className="p-2 text-left">{t('admin_aml.category')}</th>
                          <th className="p-2 text-left">{t('admin_aml.country')}</th>
                          <th className="p-2 text-left">{t('admin_aml.score')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {screenResult.matches.map((m, i) => (
                          <tr key={`${m.name}-${m.source || i}`} className="border-t">
                            <td className="p-2 font-medium">{m.name}</td>
                            <td className="p-2">{m.category}</td>
                            <td className="p-2">{m.country}</td>
                            <td className="p-2"><ScoreBar score={m.score} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-[hsl(152_45%_28%)] flex items-center gap-2" data-testid="admin-aml-no-match">
                  <CheckCircle2 className="h-4 w-4" />
                  {t('admin_aml.no_matches')}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between gap-3">
        <h2 className="font-serif text-xl text-[hsl(var(--brand-navy-2))]">{t('admin_aml.flags_title')}</h2>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList data-testid="admin-aml-filter-tabs">
            <TabsTrigger value="OPEN" data-testid="admin-aml-filter-open">{t('admin_aml.status_open')}</TabsTrigger>
            <TabsTrigger value="CLEARED" data-testid="admin-aml-filter-cleared">{t('admin_aml.status_cleared')}</TabsTrigger>
            <TabsTrigger value="ESCALATED" data-testid="admin-aml-filter-escalated">{t('admin_aml.status_escalated')}</TabsTrigger>
            <TabsTrigger value="ALL" data-testid="admin-aml-filter-all">{t('admin_aml.status_all')}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Card className="bg-white">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--secondary))]">
              <tr>
                <th className="p-3 text-left">{t('admin_aml.name_checked')}</th>
                <th className="p-3 text-left">{t('admin_aml.matched_name')}</th>
                <th className="p-3 text-left">{t('admin_aml.entity')}</th>
                <th className="p-3 text-left">{t('admin_aml.category')}</th>
                <th className="p-3 text-left">{t('admin_aml.score')}</th>
                <th className="p-3 text-left">{t('admin_aml.status_label')}</th>
                <th className="p-3 text-left">{t('admin_aml.created')}</th>
                <th className="p-3 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody data-testid="admin-aml-flags-tbody">
              {loading && (
                <tr><td colSpan={8} className="p-8 text-center text-[hsl(var(--muted-foreground))]">{t('common.loading')}</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-[hsl(var(--muted-foreground))]" data-testid="admin-aml-empty">{t('admin_aml.empty')}</td></tr>
              )}
              {items.map(f => (
                <tr key={f.id} className="border-t hover:bg-[hsl(var(--secondary))]/40" data-testid="admin-aml-flag-row">
                  <td className="p-3 font-medium">{f.name_checked}</td>
                  <td className="p-3">{f.matched_name}</td>
                  <td className="p-3 text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">{f.entity_type || (f.company_id ? 'company' : 'user')}</td>
                  <td className="p-3">{f.category}</td>
                  <td className="p-3"><ScoreBar score={f.score} /></td>
                  <td className="p-3"><StatusChip status={f.status} /></td>
                  <td className="p-3 text-xs tabular-nums text-[hsl(var(--muted-foreground))]">{fmtDate(f.created_at)}</td>
                  <td className="p-3 text-right">
                    {f.status === 'OPEN' ? (
                      <Button size="sm" variant="outline" onClick={() => openReview(f)} data-testid="admin-aml-review-button">
                        {t('admin_aml.review')}
                      </Button>
                    ) : (
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">{f.reviewer_notes ? `“${f.reviewer_notes}”` : '—'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!reviewFlag} onOpenChange={(v) => !v && setReviewFlag(null)}>
        <DialogContent data-testid="admin-aml-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-[hsl(var(--brand-navy-2))]">
              {t('admin_aml.review_title')}
            </DialogTitle>
          </DialogHeader>
          {reviewFlag && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-[hsl(var(--secondary))]/40 p-3 text-sm space-y-1">
                <div><span className="text-[hsl(var(--muted-foreground))]">{t('admin_aml.name_checked')}:</span> <strong>{reviewFlag.name_checked}</strong></div>
                <div><span className="text-[hsl(var(--muted-foreground))]">{t('admin_aml.matched_name')}:</span> {reviewFlag.matched_name}</div>
                <div><span className="text-[hsl(var(--muted-foreground))]">{t('admin_aml.category')}:</span> {reviewFlag.category} · {reviewFlag.country}</div>
                <div><span className="text-[hsl(var(--muted-foreground))]">{t('admin_aml.score')}:</span> <ScoreBar score={reviewFlag.score} /></div>
                <div><span className="text-[hsl(var(--muted-foreground))]">{t('admin_aml.reason')}:</span> {reviewFlag.reason}</div>
              </div>
              <div>
                <Label>{t('admin_aml.decision')}</Label>
                <div className="mt-2 flex gap-2" role="radiogroup">
                  {[
                    { v: 'CLEARED', label: t('admin_aml.status_cleared'), icon: CheckCircle2, cls: 'text-[hsl(152_45%_28%)]' },
                    { v: 'ESCALATED', label: t('admin_aml.status_escalated'), icon: ShieldAlert, cls: 'text-[hsl(30_90%_35%)]' },
                    { v: 'BLOCKED', label: t('admin_aml.status_blocked'), icon: XCircle, cls: 'text-[hsl(0_72%_45%)]' },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setReviewStatus(opt.v)}
                      data-testid={`admin-aml-decision-${opt.v.toLowerCase()}`}
                      className={`flex-1 rounded-lg border px-3 py-2 text-sm flex items-center justify-center gap-2 transition-colors ${reviewStatus === opt.v ? 'border-[hsl(var(--brand-navy))] bg-[hsl(var(--brand-navy))]/5' : 'hover:bg-[hsl(var(--secondary))]'}`}
                    >
                      <opt.icon className={`h-4 w-4 ${opt.cls}`} />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="aml-review-notes">{t('admin_aml.notes')}</Label>
                <Textarea
                  id="aml-review-notes"
                  data-testid="admin-aml-review-notes"
                  value={reviewNotes}
                  onChange={e => setReviewNotes(e.target.value)}
                  placeholder={t('admin_aml.notes_placeholder')}
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewFlag(null)}>{t('common.cancel')}</Button>
            <Button
              onClick={submitReview}
              disabled={reviewBusy}
              data-testid="admin-aml-review-submit"
              className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))]"
            >
              {t('admin_aml.confirm_decision')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
